/**
 * Transaction Service
 * 
 * Transaction-related API operations.
 */

import { apiClient } from '../client';
import type { 
  Transaction, 
  TransactionListResponse,
  PaginationParams,
  DateRangeFilter 
} from '../types';

export interface CreateTransactionRequest {
  organizationId: string;
  date: string;
  amount: number;
  description?: string;
  counterparty?: string;
  category?: string;
  accountCode?: string;
  vatRate?: number | null;
  receiptFileId?: string;
}

export interface UpdateTransactionRequest {
  date?: string;
  amount?: number;
  description?: string;
  counterparty?: string;
  category?: string;
  accountCode?: string;
  verified?: boolean;
}

export interface TransactionFilters extends PaginationParams, DateRangeFilter {
  category?: string;
  status?: string;
  organizationId: string;
}

export class TransactionService {
  /**
   * List transactions with filters
   */
  async list(filters: TransactionFilters): Promise<TransactionListResponse> {
    const params = new URLSearchParams();
    params.append('organization_id', filters.organizationId);
    
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.category) params.append('category', filters.category);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.offset) params.append('offset', String(filters.offset));

    return apiClient.get<TransactionListResponse>(`/transactions?${params.toString()}`);
  }

  /**
   * Get transaction by ID
   */
  async get(id: string, organizationId: string): Promise<Transaction> {
    return apiClient.get<Transaction>(`/transactions/${id}?organization_id=${organizationId}`);
  }

  /**
   * Create new transaction
   */
  async create(data: CreateTransactionRequest): Promise<Transaction> {
    return apiClient.post<Transaction>('/transactions', data);
  }

  /**
   * Update transaction
   */
  async update(id: string, data: UpdateTransactionRequest): Promise<Transaction> {
    return apiClient.put<Transaction>(`/transactions/${id}`, data);
  }

  /**
   * Delete transaction
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/transactions/${id}`);
  }
}

// Export singleton instance
export const transactionService = new TransactionService();
